import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { UserOrRoleDc, RolePermissionDc } from "@evergis/api";
import { ChipAutoComplete, ChipAutoCompleteData } from "@evergis/ui";

import { getUsersAndRoles } from "../../../../resources/security";
import { getUsername } from "../../../../ducks/user";
import { isRead } from "../../../../utils/permissions";

interface ChipsProps {
  resourceLoading?: boolean;
  resourceAcl?: RolePermissionDc[];
  onAdd?: (role: string) => void;
  onRemove?: (role: string) => void;
  setIsLoading: (state: boolean) => void;
}

export const Chips: FC<ChipsProps> = ({ resourceLoading, resourceAcl, onAdd, onRemove, setIsLoading }) => {
  const { t } = useTranslation("dialogs");
  const currentUser: string = useSelector(getUsername);
  const { data: usersAndRoles, loading: usersAndRolesLoading } = getUsersAndRoles.use();
  const [users, setUsers] = useState<UserOrRoleDc[] | undefined>();
  const [activeUsers, setActiveUsers] = useState<string[]>([]);

  useEffect(() => {
    if (users === undefined && usersAndRoles) {
      setUsers(usersAndRoles.filter(value => !value.isRole && value.aclRole !== `__${currentUser}`));
    }
  }, [currentUser, users, usersAndRoles]);

  useEffect(() => {
    if (users && resourceAcl) {
      setActiveUsers(
        resourceAcl.reduce((acc: string[], curr) => {
          const user = users.find(item => item.aclRole === curr.role);

          if (user && curr.role !== `__${currentUser}` && isRead(curr.permissions)) {
            return [...acc, user.name];
          }

          return acc;
        }, []),
      );
    }
  }, [currentUser, resourceAcl, users]);

  useEffect(() => {
    setIsLoading(resourceLoading || usersAndRolesLoading);
  }, [resourceLoading, usersAndRolesLoading, setIsLoading]);

  return (
    <ChipAutoComplete
      loading={usersAndRolesLoading || resourceLoading}
      items={users as ChipAutoCompleteData[]}
      activeItems={activeUsers}
      onSelectItem={(name: string) => {
        const user = users.find(item => item.name === name);

        onAdd && onAdd(user.aclRole);
      }}
      onRemoveItem={(name: string) => {
        const user = users.find(item => item.name === name);

        onRemove && onRemove(user.aclRole);
      }}
      slideButtonTitle={t("access.slide")}
      listButtonTitle={t("access.list")}
      placeholder={t("wmsLink.usersPlaceholder")}
      searchPlaceholder={t("access.search")}
    />
  );
};
